import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Modal,
  Form,
  Table,
  Card,
  Row,
  Col,
  Badge,
  Input,
  Space,
  Popconfirm,
  Tooltip,
  Statistic,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  DollarOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import debtorsService from '../api/debtors';
import Navbar from '../components/Navbar';
import './Debtors.css';

const Debtors = () => {
  const { t } = useTranslation('debtors');
  const { t: tCommon } = useTranslation('translation');
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    details: '',
  });
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedDebtor, setSelectedDebtor] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [detailsModal, setDetailsModal] = useState(false);
  const [debtorStatistics, setDebtorStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [stats, setStats] = useState({
    totalDebtors: 0,
    totalDebt: 0,
    totalPaid: 0,
    overdueCount: 0,
  });

  // Set RTL based on language
  useEffect(() => {
    if (i18n.language === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [i18n.language]);

  // Load debtors
  const fetchDebtors = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await debtorsService.getDebtors(params);
      const data = response.data;
      
      console.log('API Response:', response);
      console.log('Data:', data);
      
      // Data should already be an array due to interceptor
      if (Array.isArray(data)) {
        setDebtors(data);
      } else {
        setDebtors([]);
      }
    } catch (error) {
      console.error('Error fetching debtors:', error);
      message.error(tCommon('error'));
      setDebtors([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = async () => {
    try {
      const response = await debtorsService.getDebtors();
      const data = response.data;
      
      // Data should already be an array due to interceptor
      let allDebtors = Array.isArray(data) ? data : [];
      
      const totalDebt = allDebtors.reduce((sum, d) => sum + (Number(d.total_debt) || 0), 0);
      const totalPaid = allDebtors.reduce((sum, d) => sum + (Number(d.total_paid) || 0), 0);
      const overdueDebtors = allDebtors.filter(d => d.is_overdue && d.status !== 'paid');

      setStats({
        totalDebtors: allDebtors.length,
        totalDebt: Number(totalDebt).toFixed(2),
        totalPaid: Number(totalPaid).toFixed(2),
        overdueCount: overdueDebtors.length,
      });
    } catch (error) {
      console.error('Error calculating stats:', error);
    }
  };

  useEffect(() => {
    fetchDebtors();
    calculateStats();
  }, [searchTerm, filterStatus]);;

  const handleAddDebtor = () => {
    setEditingId(null);
    setFormData({ name: '', phone: '', details: '' });
    setModalVisible(true);
  };

  const handleEditDebtor = (debtor) => {
    setEditingId(debtor.id);
    setFormData({
      name: debtor.name,
      phone: debtor.phone,
      details: debtor.details || '',
    });
    setModalVisible(true);
  };

  const handleSaveDebtor = async () => {
    if (!formData.name || !formData.phone) {
      message.warning(t('namePhoneRequired'));
      return;
    }

    try {
      if (editingId) {
        await debtorsService.updateDebtor(editingId, formData);
      } else {
        await debtorsService.createDebtor(formData);
      }
      setModalVisible(false);
      fetchDebtors();
      calculateStats();
      message.success(editingId ? t('debtUpdated') : t('debtAdded'));
    } catch (error) {
      console.error('Error saving debtor:', error);
      message.error(error.response?.data?.detail || t('failedToSaveDebtor'));
    }
  };

  const handleDeleteDebtor = async (id) => {
    try {
      await debtorsService.deleteDebtor(id);
      fetchDebtors();
      calculateStats();
      message.success(t('debtDeleted'));
    } catch (error) {
      console.error('Error deleting debtor:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || t('failedToDeleteDebtor');
      
      // Check if error is about having invoices
      if (error.response?.data?.errorCode === 'cannot_delete_debtor_with_sales') {
        message.error(t('cannotDeleteCustomerWithInvoices'));
      } else {
        message.error(errorMsg);
      }
    }
  };

  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      message.warning(t('invalidAmount'));
      return;
    }

    try {
      await debtorsService.addPayment(selectedDebtor.id, parseFloat(paymentAmount));
      setPaymentModal(false);
      setPaymentAmount('');
      setSelectedDebtor(null);
      fetchDebtors();
      calculateStats();
      message.success(t('paymentRecorded'));
    } catch (error) {
      console.error('Error recording payment:', error);
      message.error(t('failedToRecordPayment'));
    }
  };

  const handleSendWhatsApp = async (debtor) => {
    try {
      const response = await debtorsService.sendWhatsApp(debtor.id);
      const whatsappUrl = response.data.whatsapp_url;
      window.open(whatsappUrl, '_blank', 'width=800,height=600');
      message.success(t('whatsappMessagePrepared'));
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      message.error(t('failedToSendWhatsApp'));
    }
  };

  const handlePrintStatement = async (debtor) => {
    try {
      const response = await debtorsService.getDebtorStatement(debtor.id);
      const statement = response.data;

      const formatDate = (value) => new Date(value).toLocaleString(i18n.language || 'en');
      const formatUsd = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;
      const formatLbp = (value) => `${Math.round(parseFloat(value) || 0).toLocaleString('en-US')} ${tCommon('lbpSymbol')}`;

      const debtInvoiceRows = statement.debt_invoices.length > 0
        ? statement.debt_invoices.map((invoice) => `
            <tr>
              <td>${invoice.receipt_number}</td>
              <td>${formatDate(invoice.sale_date)}</td>
              <td>${formatUsd(invoice.total_usd)}</td>
              <td>${formatLbp(invoice.total_lbp)}</td>
            </tr>
          `).join('')
        : `<tr><td colspan="4">${t('noDataAvailable', { defaultValue: 'No debt invoices' })}</td></tr>`;

      const paymentRows = statement.payments.length > 0
        ? statement.payments.map((payment) => `
            <tr>
              <td>${formatDate(payment.created_at)}</td>
              <td>${formatUsd(payment.amount)}</td>
              <td>${payment.notes || '-'}</td>
            </tr>
          `).join('')
        : `<tr><td colspan="3">${t('noDataAvailable', { defaultValue: 'No payments recorded' })}</td></tr>`;

      const printWindow = window.open('', '', 'width=960,height=720');
      if (!printWindow) {
        message.error(tCommon('error'));
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html dir="${i18n.language === 'ar' ? 'rtl' : 'ltr'}">
          <head>
            <meta charset="UTF-8" />
            <title>${t('accountStatement', { defaultValue: 'Account Statement' })}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
              .header { margin-bottom: 24px; border-bottom: 2px solid #ddd; padding-bottom: 16px; }
              .header h1 { margin: 0 0 8px; }
              .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 20px 0; }
              .card { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
              .section { margin-top: 28px; }
              .section h2 { margin: 0 0 12px; font-size: 20px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
              th { background: #f5f5f5; }
              [dir="rtl"] th, [dir="rtl"] td { text-align: right; }
              .muted { color: #666; font-size: 12px; }
              @media print {
                body { margin: 12px; }
                .summary { grid-template-columns: repeat(3, 1fr); }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${t('accountStatement', { defaultValue: 'Account Statement' })}</h1>
              <div><strong>${t('name')}:</strong> ${statement.debtor_name}</div>
              <div><strong>${t('phone')}:</strong> ${statement.debtor_phone}</div>
              <div class="muted">${t('statementGeneratedAt', { defaultValue: 'Generated at' })}: ${formatDate(new Date().toISOString())}</div>
            </div>

            <div class="summary">
              <div class="card"><strong>${t('totalDebt')}:</strong><div>${formatUsd(statement.total_debt)}</div></div>
              <div class="card"><strong>${t('totalPaid')}:</strong><div>${formatUsd(statement.total_paid)}</div></div>
              <div class="card"><strong>${t('remaining')}:</strong><div>${formatUsd(statement.remaining_debt)}</div></div>
            </div>

            <div class="section">
              <h2>${t('debtInvoices')}</h2>
              <table>
                <thead>
                  <tr>
                    <th>${t('receiptNumber', { defaultValue: 'Receipt #' })}</th>
                    <th>${tCommon('date')}</th>
                    <th>USD</th>
                    <th>LBP</th>
                  </tr>
                </thead>
                <tbody>${debtInvoiceRows}</tbody>
              </table>
            </div>

            <div class="section">
              <h2>${t('paymentHistory', { defaultValue: 'Payment History' })}</h2>
              <table>
                <thead>
                  <tr>
                    <th>${tCommon('date')}</th>
                    <th>${t('paymentAmount', { defaultValue: 'Payment Amount' })}</th>
                    <th>${t('details')}</th>
                  </tr>
                </thead>
                <tbody>${paymentRows}</tbody>
              </table>
            </div>

            <script>
              window.onload = function() { window.print(); };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing statement:', error);
      message.error(t('failedToLoadStatement', { defaultValue: 'Failed to load statement' }));
    }
  };
  const handleViewDetails = async (debtor) => {
    try {
      setLoadingStats(true);
      setSelectedDebtor(debtor);
      setDetailsModal(true);
      
      const response = await debtorsService.getDebtorStatistics(debtor.id);
      setDebtorStatistics(response.data);
    } catch (error) {
      console.error('Error fetching debtor statistics:', error);
      message.error(tCommon('error'));
    } finally {
      setLoadingStats(false);
    }
  };
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'red', text: t('statusPending') },
      partial: { color: 'orange', text: t('statusPartial') },
      paid: { color: 'green', text: t('statusPaid') },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge color={config.color} text={config.text} />;
  };

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: t('phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => (
        <Tooltip title={t('callDebtor')}>
          <a href={`tel:${text}`}>
            <PhoneOutlined /> {text}
          </a>
        </Tooltip>
      ),
    },
    {
      title: t('totalDebt'),
      dataIndex: 'total_debt',
      key: 'total_debt',
      render: (amount) => <span style={{ color: '#d32f2f' }}>${(parseFloat(amount) || 0).toFixed(2)}</span>,
    },
    {
      title: t('totalPaid'),
      dataIndex: 'total_paid',
      key: 'total_paid',
      render: (amount) => <span style={{ color: '#388e3c' }}>${(parseFloat(amount) || 0).toFixed(2)}</span>,
    },
    {
      title: t('remaining'),
      dataIndex: 'remaining_debt',
      key: 'remaining_debt',
      render: (amount) => <strong style={{ color: '#f57c00' }}>${(parseFloat(amount) || 0).toFixed(2)}</strong>,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <div>
          {getStatusBadge(status)}
          {record.is_overdue && <Badge color="volcano" text={t('overdue')} style={{ marginLeft: 8 }} />}
        </div>
      ),
    },
    {
      title: tCommon('actions'),
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title={t('viewDetails')}>
            <Button
              type="primary"
              size="small"
              onClick={() => handleViewDetails(record)}
            >
              {t('viewDetails')}
            </Button>
          </Tooltip>
          <Tooltip title={t('payment')}>
            <Button
              type="default"
              size="small"
              icon={<DollarOutlined />}
              onClick={() => {
                setSelectedDebtor(record);
                setPaymentModal(true);
                setPaymentAmount('');
              }}
            >
              {t('payment')}
            </Button>
          </Tooltip>
          <Tooltip title={t('accountStatement', { defaultValue: 'Account Statement' })}>
            <Button
              type="default"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintStatement(record)}
            >
              {t('accountStatement', { defaultValue: 'Account Statement' })}
            </Button>
          </Tooltip>
          <Tooltip title={tCommon('edit')}>
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditDebtor(record)}
            >
              {tCommon('edit')}
            </Button>
          </Tooltip>
          <Tooltip title={t('sendWhatsApp')}>
            <Button
              type="default"
              size="small"
              icon={<WhatsAppOutlined />}
              style={{ color: '#25d366' }}
              onClick={() => handleSendWhatsApp(record)}
            >
              WhatsApp
            </Button>
          </Tooltip>
          <Popconfirm
            title={tCommon('deleteConfirm')}
            description={tCommon('deleteConfirm')}
            onConfirm={() => handleDeleteDebtor(record.id)}
            okText={tCommon('yes')}
            cancelText={tCommon('no')}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              {tCommon('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Navbar />
      <div className="debtors-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row' }}>
        <h1 style={{ margin: 0 }}>{t('debtorsManagement')}</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddDebtor}
          size="large"
        >
          {t('addDebtor')}
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalDebtors')}
              value={stats.totalDebtors}
              prefix={<PhoneOutlined />}
              styles={{ content: { color: '#333' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalDebt')}
              value={stats.totalDebt}
              prefix="$"
              styles={{ content: { color: '#d32f2f' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalPaid')}
              value={stats.totalPaid}
              prefix="$"
              styles={{ content: { color: '#388e3c' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('overdueCount')}
              value={stats.overdueCount}
              styles={{ content: { color: '#f57c00' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Input.Search
              placeholder={t('searchByNameOrPhone')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">{t('allStatus')}</option>
              <option value="pending">{t('statusPending')}</option>
              <option value="partial">{t('statusPartial')}</option>
              <option value="paid">{t('statusPaid')}</option>
            </select>
          </Col>
        </Row>
      </Card>

      {/* Debtors Table */}
      <Card loading={loading}>
        <Table
          columns={columns}
          dataSource={debtors}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Add/Edit Debtor Modal */}
      <Modal
        title={editingId ? t('editDebtor') : t('addDebtor')}
        open={modalVisible}
        onOk={handleSaveDebtor}
        onCancel={() => setModalVisible(false)}
        okText={tCommon('save')}
        cancelText={tCommon('cancel')}
      >
        <Form layout="vertical">
          <Form.Item label={t('name')}>
            <Input
              placeholder={t('debtorName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </Form.Item>
          <Form.Item label={t('phone')}>
            <Input
              placeholder={t('debtorPhone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Form.Item>
          <Form.Item label={t('details')}>
            <Input.TextArea
              placeholder={t('debtorDetails')}
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title={t('recordPayment')}
        open={paymentModal}
        onOk={handlePayment}
        onCancel={() => {
          setPaymentModal(false);
          setPaymentAmount('');
          setSelectedDebtor(null);
        }}
        okText={tCommon('save')}
        cancelText={tCommon('cancel')}
      >
        {selectedDebtor && (
          <Form layout="vertical">
            <Form.Item>
              <div style={{ marginBottom: 16 }}>
                <p><strong>{t('name')}:</strong> {selectedDebtor.name}</p>
                <p><strong>{t('totalDebt')}:</strong> ${(parseFloat(selectedDebtor.total_debt) || 0).toFixed(2)}</p>
                <p><strong>{t('totalPaid')}:</strong> ${(parseFloat(selectedDebtor.total_paid) || 0).toFixed(2)}</p>
                <p><strong>{t('remaining')}:</strong> ${(parseFloat(selectedDebtor.remaining_debt) || 0).toFixed(2)}</p>
              </div>
            </Form.Item>
            <Form.Item label={t('paymentAmount')}>
              <Input
                type="number"
                placeholder="0.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                min="0"
                step="0.01"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Details Modal */}
      <Modal
        title={t('customerStatistics')}
        open={detailsModal}
        onCancel={() => {
          setDetailsModal(false);
          setDebtorStatistics(null);
          setSelectedDebtor(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailsModal(false);
            setDebtorStatistics(null);
            setSelectedDebtor(null);
          }}>
            {tCommon('close')}
          </Button>
        ]}
        width={600}
      >
        {loadingStats ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <span>Loading...</span>
          </div>
        ) : debtorStatistics ? (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, borderBottom: '2px solid #1890ff', paddingBottom: 8 }}>
                {t('salesInfo')}
              </h3>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic 
                    title={t('totalInvoices')} 
                    value={debtorStatistics.total_invoices}
                    prefix="📄"
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('debtInvoices')} 
                    value={debtorStatistics.debt_invoices}
                    prefix="📋"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('totalSalesUSD')} 
                    value={debtorStatistics.total_sales_usd}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('totalSalesLBP')} 
                    value={debtorStatistics.total_sales_lbp}
                    precision={0}
                    suffix="ل.ل"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, borderBottom: '2px solid #ff4d4f', paddingBottom: 8 }}>
                {t('debtInfo')}
              </h3>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic 
                    title={t('debtUSD')} 
                    value={debtorStatistics.debt_usd}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('debtLBP')} 
                    value={debtorStatistics.debt_lbp}
                    precision={0}
                    suffix="ل.ل"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('totalPaid')} 
                    value={debtorStatistics.total_paid}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title={t('remaining')} 
                    value={debtorStatistics.remaining_debt}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <span>No data available</span>
          </div>
        )}
      </Modal>
      </div>
    </>
  );
};

export default Debtors;
